package org.example.project.screens_driver

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.example.project.engineslot.SLOT_BEREICH_WA
import org.example.project.engineslot.SLOT_BEREICH_WE
import org.example.project.service.InfrastrukturApi
import org.example.project.service.YardEngineApi
import org.example.project.service.YardFlowApi
import org.example.project.service.ZustandManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.example.project.utils.LocalizationManager
import org.example.project.yard.yardPinBestaetigt
import org.example.project.yard.yardPinCode
import org.example.project.yard.HofMapView
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.js.Date

private const val DRIVER_TOR_ASSIGNED_POPUP_CONFIRMED = "driverTorAssignedPopupConfirmed"
private const val MOBILE_BEARBEITER_USER_ID = "mobile.bearbeiterUserId"

@Composable
fun FahrerLobbyScreen(
    eintrag: All?,
    isWeAuftrag: Boolean? = null,
    footerActionLabel: String? = null,
    onFooterAction: (() -> Unit)? = null,
    ankunftBestaetigungErforderlich: Boolean = false,
    parkplaetzeImYardBenutzen: Boolean = true,
    onAnkunftBestaetigt: (() -> Unit)? = null,
    onWaDokumenteBestaetigt: ((All) -> Unit)? = null,
    onYardVerlassen: (() -> Unit)? = null
) {
    Style(FahrerLobbyActionStyles)
    val t: (String) -> String = { LocalizationManager.get(it) }
    var currentEintrag by remember(eintrag?.id, eintrag?.lfdNr) { mutableStateOf(eintrag) }

    LaunchedEffect(eintrag) {
        currentEintrag = mergeFahrerLobbyEintrag(currentEintrag, eintrag)
    }

    val e = currentEintrag ?: return
    val scope = rememberCoroutineScope()
    val lobbyStandortId = fahrerLobbyStandortId(e)
    val saveAsWe = isWeAuftrag ?: (e.auftragsart == SLOT_BEREICH_WE || e.lieferscheinnummer.isBlank())
    val driverWasCalledToGate = e.mobileBearbeiterUserId.isNotBlank()
    val hasAssignedTor = e.zustand == ZustandManager.TOR_ZUGEWIESEN &&
        driverWasCalledToGate &&
        e.torSystemgesetztUm.isBlank()
    val occupiedTorIds = e.tor
        ?.takeIf { e.zustand >= ZustandManager.TOR_ZUGEWIESEN && e.zustand < ZustandManager.ABGESCHLOSSEN }
        ?.let { setOf(it) }
        ?: emptySet()
    val showAssignedPopup = hasAssignedTor
    val showPinNotice = e.zustand == ZustandManager.PIN_ABFRAGE && !yardPinBestaetigt(e)
    val showWaDokumentabschluss = !saveAsWe &&
        e.zustand == ZustandManager.ABGESCHLOSSEN &&
        e.fahrerDokumenteBestaetigungOffen
    val showFahrerAbschluss = e.zustand >= ZustandManager.ABGESCHLOSSEN &&
        (saveAsWe || e.dokumenteFertig) &&
        !showWaDokumentabschluss
    var waDokumentabschlussSpeichern by remember(e.id, e.lfdNr, e.zustand) { mutableStateOf(false) }
    var parkplaetze by remember(lobbyStandortId, e.firmenId) { mutableStateOf<List<All>>(emptyList()) }

    if (showFahrerAbschluss) {
        FahrerLobbyAbschlussScreen(
            isWeAuftrag = saveAsWe,
            onYardVerlassen = onYardVerlassen
        )
        return
    }

    LaunchedEffect(lobbyStandortId, e.firmenId, ankunftBestaetigungErforderlich, parkplaetzeImYardBenutzen) {
        if (!ankunftBestaetigungErforderlich || !parkplaetzeImYardBenutzen) {
            parkplaetze = emptyList()
            return@LaunchedEffect
        }
        parkplaetze = runCatching {
            InfrastrukturApi.loadParkplaetze(e.firmenId)
                .filter { it.standort == lobbyStandortId && it.gesperrt != true }
                .sortedWith(compareBy<All> { it.id.toIntOrNull() ?: Int.MAX_VALUE }.thenBy { it.id })
        }.getOrDefault(emptyList())
    }
    val parkplatzId = preferredFahrerLobbyParkplatzId(e, parkplaetze)
    val parkplatzFlowAktiv = parkplaetzeImYardBenutzen && parkplatzId != null

    fun fahrerHatAngesetztBestaetigt() {
        scope.launch {
            val latest = currentEintrag ?: e
            val artCode = if (saveAsWe) SLOT_BEREICH_WE else SLOT_BEREICH_WA
                val neu = YardFlowApi.fahrerBestaetigtAngesetzt(
                    auftrag = latest.copy(
                        uhrzeitTorvergabe = latest.uhrzeitTorvergabe.ifBlank { fahrerLobbyCurrentTime() },
                        driverTorAssignedPopupConfirmed = true
                    ),
                    artCode = artCode,
                    firmenId = latest.firmenId
                )
            currentEintrag = neu
        }
    }

    fun fahrerBestaetigtWaDokumentabschluss() {
        if (waDokumentabschlussSpeichern) return
        scope.launch {
            waDokumentabschlussSpeichern = true
        val latest = currentEintrag ?: e
        YardEngineApi.waDokumentabschluss(
            auftrag = latest.copy(fahrerBestaetigt = true),
            firmenId = latest.firmenId
        )?.let { neu ->
                currentEintrag = neu
                onWaDokumenteBestaetigt?.invoke(neu) ?: onYardVerlassen?.invoke()
            }
            waDokumentabschlussSpeichern = false
        }
    }

    if (showWaDokumentabschluss) {
        FahrerLobbyWaDokumentabschlussNotice(
            saving = waDokumentabschlussSpeichern,
            onConfirm = { fahrerBestaetigtWaDokumentabschluss() }
        )
        return
    }

    Div({
        style {
            width(100.percent)
            height(100.percent)
            minHeight(0.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Color.transparent)
            color(Color.white)
            overflow("hidden")
        }
    }) {
        FahrerLobbyWeHeader(entry = e)

        Div({
            style {
                flex(1)
                minHeight(0.px)
                overflow("hidden")
            }
        }) {
            HofMapView(
                standortId = lobbyStandortId,
                firmenId = e.firmenId,
                highlightTor = e.tor.takeIf { it in occupiedTorIds },
                highlightParkplatz = parkplatzId.takeIf { ankunftBestaetigungErforderlich && parkplatzFlowAktiv },
                 occupiedTorIds = occupiedTorIds,
                 fitToBounds = true,
                 freePanZoom = true,
                 localizeLabels = true
             )
        }

        if (ankunftBestaetigungErforderlich || showAssignedPopup || showPinNotice || showWaDokumentabschluss || (footerActionLabel != null && onFooterAction != null)) {
            Div({
                style {
                    padding(6.px, 10.px)
                    backgroundColor(Color.transparent)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                    flexShrink(0)
                }
            }) {
                when {
                    ankunftBestaetigungErforderlich && onAnkunftBestaetigt != null -> FahrerLobbyActionBarNotice(
                        text = if (parkplatzFlowAktiv) {
                            t("driver_lobby_welcome_park_assigned")
                        } else {
                            t("driver_lobby_welcome_confirm_parked")
                        },
                        actionLabel = if (parkplatzFlowAktiv) t("driver_lobby_arrived") else t("driver_lobby_parked"),
                        onAction = onAnkunftBestaetigt
                    )

                    showAssignedPopup -> FahrerLobbyActionBarNotice(
                        text = t("driver_lobby_dock_at_gate").replace("{gate}", e.tor?.toString().orEmpty()),
                        actionLabel = t("driver_lobby_docked"),
                        onAction = { fahrerHatAngesetztBestaetigt() }
                    )

                    showPinNotice -> FahrerLobbyActionBarPassiveNotice(
                        text = t("driver_lobby_pin_notice").replace("{pin}", yardPinCode(e)),
                    )

                    footerActionLabel != null && onFooterAction != null -> Div({
                        style {
                            property("margin-left", "auto")
                        }
                    }) {
                        LumaticaButton(
                            label = footerActionLabel,
                            onClick = onFooterAction,
                            variant = LumaticaButtonVariant.Action,
                            heightPx = 24,
                            paddingH = 8
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun FahrerLobbyAbschlussScreen(
    isWeAuftrag: Boolean,
    onYardVerlassen: (() -> Unit)?
) {
    val t: (String) -> String = { LocalizationManager.get(it) }
    Div({
        style {
            width(100.percent)
            height(100.percent)
            minHeight(0.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            justifyContent(JustifyContent.FlexStart)
            alignItems(AlignItems.Center)
            gap(18.px)
            padding(26.px, 18.px, 18.px, 18.px)
            boxSizing("border-box")
            backgroundColor(Color.transparent)
            color(Color.white)
            textAlign("center")
        }
    }) {
        Span({
            style {
                maxWidth(420.px)
                fontSize(15.px)
                fontWeight("800")
                lineHeight("1.5")
                textAlign("left")
            }
        }) {
            Text(if (isWeAuftrag) t("driver_lobby_unloading_finished") else t("driver_lobby_loading_finished"))
            Div { Text(t("driver_lobby_leave_ramp")) }
            Div { Text(t("driver_lobby_good_trip")) }
        }
        if (onYardVerlassen != null) {
            Div({ style { width(100.percent); maxWidth(320.px) } }) {
                LumaticaButton(
                    label = t("verstanden"),
                    onClick = onYardVerlassen,
                    variant = LumaticaButtonVariant.Action,
                    heightPx = 42
                )
            }
        }
    }
}

@Composable
private fun FahrerLobbyWaDokumentabschlussNotice(
    saving: Boolean,
    onConfirm: () -> Unit
) {
    val t: (String) -> String = { LocalizationManager.get(it) }
    Div({
        style {
            width(100.percent)
            height(100.percent)
            minWidth(0.px)
            minHeight(0.px)
            padding(22.px)
            property("box-sizing", "border-box")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.FlexStart)
            gap(22.px)
            backgroundColor(Color.transparent)
            color(Color.white)
            textAlign("center")
        }
    }) {
        Span({
            style {
                minWidth(0.px)
                color(Color.white)
                maxWidth(520.px)
                fontSize(15.px)
                fontWeight("800")
                lineHeight("1.5")
                textAlign("left")
            }
        }) {
            Text(t("driver_lobby_loading_finished"))
            Div { Text(t("driver_lobby_leave_ramp")) }
            Div { Text(t("driver_lobby_good_trip")) }
        }
        Div({ style { width(100.percent); maxWidth(320.px) } }) {
            LumaticaButton(
                label = if (saving) t("driver_saving") else t("verstanden"),
                onClick = onConfirm,
                variant = LumaticaButtonVariant.Action,
                enabled = !saving,
                heightPx = 42
            )
        }
    }
}

@Composable
private fun FahrerLobbyActionBarNotice(
    text: String,
    actionLabel: String,
    onAction: () -> Unit
) {
    Div({
        classes(FahrerLobbyActionStyles.actionBarNotice)
        style {
            width(100.percent)
            minWidth(0.px)
            height(32.px)
            padding(3.px, 6.px, 3.px, 10.px)
            borderRadius(8.px)
            background("linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%), #111827")
            property("box-sizing", "border-box")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
        }
    }) {
        Span({
            style {
                flex(1)
                minWidth(0.px)
                color(Color.white)
                fontSize(13.px)
                fontWeight("800")
                overflow("hidden")
                property("text-overflow", "ellipsis")
                property("white-space", "nowrap")
            }
        }) {
            Text(text)
        }
        Div({ style { width(138.px); flexShrink(0) } }) {
            LumaticaButton(
                label = actionLabel,
                onClick = onAction,
                variant = LumaticaButtonVariant.Action,
                heightPx = 24,
                paddingH = 8
            )
        }
    }
}

@Composable
private fun FahrerLobbyActionBarPassiveNotice(
    text: String
) {
    Div({
        classes(FahrerLobbyActionStyles.actionBarNotice)
        style {
            width(100.percent)
            minWidth(0.px)
            minHeight(32.px)
            padding(6.px, 10.px)
            borderRadius(8.px)
            background("linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%), #111827")
            property("box-sizing", "border-box")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
        }
    }) {
        Span({
            style {
                flex(1)
                minWidth(0.px)
                color(Color.white)
                fontSize(13.px)
                fontWeight("800")
                lineHeight("1.25")
            }
        }) {
            Text(text)
        }
    }
}

private object FahrerLobbyActionStyles : StyleSheet() {
    val redBorderBlink by keyframes {
        from { property("border-color", "#ff2d2d") }
        50.percent { property("border-color", "rgba(255,45,45,0.28)") }
        to { property("border-color", "#ff2d2d") }
    }

    val actionBarNotice by style {
        border(2.px, LineStyle.Solid, Color("#ff2d2d"))
        property("animation", "${redBorderBlink.name} 780ms ease-in-out infinite")
    }
}

private fun fahrerLobbyCurrentTime(): String {
    val date = Date()
    return "${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}"
}

private fun preferredFahrerLobbyParkplatzId(entry: All, parkplaetze: List<All>): Int? {
    entry.parkplatzId.takeIf { it > 0 }?.let { return it }
    val freieParkplaetze = parkplaetze
        .filter { it.belegt != true && it.gesperrt != true }
        .sortedWith(compareBy<All> { it.id.toIntOrNull() ?: Int.MAX_VALUE }.thenBy { it.id })
    if (freieParkplaetze.isEmpty()) return null
    val tor = entry.tor
    return freieParkplaetze
        .firstOrNull { tor != null && it.tore.contains(tor) }
        ?.id
        ?.toIntOrNull()
        ?: freieParkplaetze.firstOrNull()?.id?.toIntOrNull()
}

private fun mergeFahrerLobbyEintrag(current: All?, incoming: All?): All? {
    if (incoming == null) return current
    if (current == null) return incoming
    if (!sameFahrerLobbyEintrag(current, incoming)) return incoming

    val currentHasMoreProgress = current.zustand > incoming.zustand ||
        (current.zustand == incoming.zustand &&
            current.driverTorAssignedPopupConfirmed &&
            !incoming.driverTorAssignedPopupConfirmed)

    return if (currentHasMoreProgress) current else incoming
}

private fun sameFahrerLobbyEintrag(a: All, b: All): Boolean {
    if (a.id.isNotBlank() && b.id.isNotBlank()) return a.id == b.id
    if (a.lfdNr > 0 && b.lfdNr > 0) return a.lfdNr == b.lfdNr
    if (a.lieferscheinnummer.isNotBlank() && b.lieferscheinnummer.isNotBlank()) {
        return a.lieferscheinnummer == b.lieferscheinnummer
    }
    return false
}
